/**
 * Bus simple para que las skills CRUD del copiloto avisen a los tabs
 * cuando hubo una mutación exitosa. Los tabs se suscriben para
 * re-fetchear y opcionalmente scrollear al elemento afectado.
 */

export type CopilotMutationScope =
  | "products"
  | "categories"
  | "clients"
  | "reservations"
  | "orders"
  | "additions";

export type CopilotMutationAction =
  | "created"
  | "updated"
  | "archived"
  | "deleted"
  | "closed";

export type CopilotMutation = {
  scope: CopilotMutationScope;
  action: CopilotMutationAction;
  restaurantId: string;
  /** ID del recurso afectado (opcional, para scroll-into-view). */
  id?: string;
  /** Datos extras que el listener pueda querer usar (ej. nombre). */
  meta?: Record<string, unknown>;
};

const EVENT_NAME = "orkestria:copilot-mutation";

export function emitCopilotMutation(m: CopilotMutation) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: m }));
}

export function onCopilotMutation(
  cb: (m: CopilotMutation) => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (e: Event) => {
    const ev = e as CustomEvent<CopilotMutation>;
    cb(ev.detail);
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

/**
 * Highlight visual en el elemento afectado (después del refetch).
 * Resalta con un anillo + scroll-into-view smooth.
 */
export function highlightMutatedElement(
  selector: string,
  durationMs = 1800,
): void {
  if (typeof window === "undefined") return;
  // En móvil: pedir al copiloto que minimice el chat para que el highlight sea visible.
  window.dispatchEvent(new CustomEvent("orkestria:copilot-minimize-for-result"));
  // Pequeño delay para esperar que el DOM se actualice tras el refetch
  setTimeout(() => {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Pulse visual con outline
    const previousOutline = el.style.outline;
    const previousOutlineOffset = el.style.outlineOffset;
    const previousTransition = el.style.transition;
    el.style.transition = "outline 0.3s ease, outline-offset 0.3s ease";
    el.style.outline = "3px solid var(--primary, #6366f1)";
    el.style.outlineOffset = "4px";
    setTimeout(() => {
      el.style.outline = previousOutline;
      el.style.outlineOffset = previousOutlineOffset;
      // restaurar transition después de la animación
      setTimeout(() => {
        el.style.transition = previousTransition;
      }, 350);
    }, durationMs);
  }, 250);
}
